import { OpenAPI } from '@/api/client'
import { useAuthStore } from '@/stores/AuthStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'

export function useOpenApiConfiguration() {
  const authStore = useAuthStore()
  const noAuthEnpoints = [
    // the noAuthEndpoints have to be specified exactly while userEndpoints are defined by the route prefix
    // and everything else is a device endpoint
    '/device/upgrade/appv1/appv2',
    '/auth/login',
    '/auth/code/login',
    '/auth/user/login',
    '/auth/user/resetPassword',
    '/registration/company', // deprecated
    '/registration/v2/company',
  ]
  const { host } = storeToRefs(useConfigStore())

  watch(host, (newHost) => {
    console.log('watching for host change', newHost)
    OpenAPI.BASE = newHost
  })

  const userEndpointPrefixes = [
    '/absence',
    '/correction',
    '/timesheet',
    '/auth/user/logout',
    '/auth/user/officeSessionUrl',
    '/survey',
    '/possibleAbsenceSubstitutes',
    '/travelLog/showMany',
    '/admin/nfc-tags',
    '/shift-plan',
  ]

  function lookupToken(url: string): string {
    if (noAuthEnpoints.includes(url)) {
      console.info(url + ' using no token')
      return ''
    }
    if (userEndpointPrefixes.some((prefix) => url.startsWith(prefix))) {
      console.info(url + ' using user token')
      if (!authStore.user_token) {
        throw new Error(
          `Trying to make request to ${url}, but user_token is not set`,
        )
      }
      return authStore.user_token
    }

    console.info(url + ' using device token')
    if (!authStore.device_token) {
      throw new Error(
        `Trying to make request to ${url}, but device_token is not set`,
      )
    }
    return authStore.device_token
  }

  OpenAPI.BASE = host.value
  // eslint-disable-next-line
  OpenAPI.TOKEN = async (apiRequestOptions) => {
    const url = apiRequestOptions.url
    return lookupToken(url)
  }

  type deviceInfo = {
    app_build: string
    app_version: string
    device_model: string
    operating_system: string
    os_version: string
  }

  Promise.all([App.getInfo(), Device.getInfo()])
    .then((value) => {
      const appInfo = value[0]
      const deviceInfo = value[1]
      const info: deviceInfo = {
        app_build: appInfo.build,
        app_version: appInfo.version,
        device_model: deviceInfo.model,
        operating_system: deviceInfo.operatingSystem,
        os_version: deviceInfo.osVersion,
      }
      OpenAPI.HEADERS = {
        'device-information': JSON.stringify(info),
      }
    })
    // will throw "not implemented on web" in browser
    .catch(
      (err) =>
        (OpenAPI.HEADERS = {
          'device-information': err,
        }),
    )
}
